/**
 * The COP header's SYSTEM verdict.
 *
 * A pure function, and deliberately a narrow one: it is not given the alerts, so
 * it cannot key on them. That is the whole point. The rule used to be
 * `!connected || contextDataDegraded || activeAlertCount > 0`, and the third
 * term meant a single unacknowledged alert - any alert, any age, any severity -
 * pinned the header to DEGRADED until somebody cleared it.
 *
 * On 2026-09-08 prod read DEGRADED on fifteen alerts, all stale from the
 * previous afternoon, while `/health` returned `status: healthy` with an empty
 * `warnings` array and postgres, redis and MQTT all connected. Nothing was
 * wrong. A header that says DEGRADED when nothing is wrong is a header an
 * operator stops reading, which costs exactly the alert the badge exists to
 * carry.
 *
 * DEGRADED is about the SYSTEM: can the platform see the fleet and serve the
 * screen. The FLEET has its own indicator two elements to the left - the alert
 * bell (`CopAlertBell`). It counts a NARROWER list than the old rule did:
 * `status === 'active'`, i.e. unacknowledged, where this rule counted
 * `!== 'resolved'`. That gap was part of the bug - an alert somebody had already
 * acknowledged had stopped the bell flashing but still held the header at
 * DEGRADED, so the two indicators disagreed about the same alert.
 *
 * `TopBar` folds in the three dependencies it polls from `/health` (database,
 * cache, MQTT) plus the socket, so the word and the dots beneath it cannot
 * disagree. What this function adds is the half the health endpoint cannot see:
 * whether the live-update socket reached THIS browser, and whether the API is
 * actually serving the context the COP is built on.
 */
export interface SystemStatusInput {
  /** The live-update WebSocket is connected. Health cannot see this per browser. */
  liveUpdates: boolean;
  /**
   * At least one of the COP's context fetches is failing (missions,
   * maintenance, geofences, sites, drone registry). A 200 on `/health` does not
   * mean the API is serving these.
   */
  contextDataFailed: boolean;
  /**
   * The mission queue's own coverage lookup came back unanswerable (#650).
   *
   * A THIRD TERM ON A RULE THIS FILE ARGUES AGAINST WIDENING, so it earns its place on the same
   * test the other two pass: is this the platform failing to serve the screen, or is it a fact
   * about the fleet? It is the first. `GET /api/missions` answered 200 with the coverage fields
   * OMITTED because its own database read threw (#471), which is `contextDataFailed` in every
   * respect except that the request did not fail - and while it holds, `missionCoverageLock`
   * refuses STOP on every row of the panel.
   *
   * It is also NOT the 2026-09-08 shape. That incident was a stale alert count pinning the header
   * indefinitely; this clears itself on the next poll that can answer, and the panel underneath is
   * already showing an amber bar saying so. The header agreeing with the panel below it is the
   * whole of #650's second defect, moved to the surface the golden flow actually runs on.
   */
  coverageUnread: boolean;
}

export type SystemStatus = 'nominal' | 'degraded';

export function copSystemStatus(input: SystemStatusInput): SystemStatus {
  return !input.liveUpdates || input.contextDataFailed || input.coverageUnread ? 'degraded' : 'nominal';
}
